import LocationSearchIcon from "@/assets/icons/LocationSearch.module.svg";
import type { HighlightType } from "@/grid/GridCellHighlightProvider";
import { usePinnedGridHighlight } from "@/grid/usePinnedGridHighlight";
import { useClickOutsideWithStateMultiple } from "@/hooks";
import { useExportStatus } from "@/hooks/useExportStatus";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import { OffsetControl } from "@/overlay/components/QuickAccessLabel/ControlComponents/OffsetControl";
import { TitleStyleControl } from "@/overlay/components/QuickAccessLabel/ControlComponents/TitleStyleControl";
import { LabelDescription } from "@/overlay/components/QuickAccessLabel/LabelDescription";
import { LabelTitle } from "@/overlay/components/QuickAccessLabel/LabelTitle";
import { LoadingLabelFrame } from "@/overlay/components/QuickAccessLabel/LoadingLabelFrame";
import { OffsetDescription } from "@/overlay/components/QuickAccessLabel/OffsetDescription";
import styles from "@/overlay/components/QuickAccessLabel/QuickAccessLabel.module.scss";
import { useOverlay } from "@/overlay/overlays/default/ViewportOverlayProvider";
import { classNames } from "@/utility/jsx";
import { convertToCSSProperties } from "@/utility/style";
import { t } from "@lingui/core/macro";
import type { JSONInitDate } from "@mm/api.meteomatics.com";
import type { LocationResult } from "@mm/geocoder.meteomatics.com/models";
import { type ElementStyle, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { MmTooltipWrapper } from "@mm/zeus-react";
import { DateTime } from "luxon";
import { useRef, useState } from "react";

export type MapLabelProps = {
  title: string | null;
  displayTime?: DateTime;
  timezone: GuiTimeZone;
  isLoading?: boolean;
  gridHighlightType?: HighlightType;
  gridHighlightId?: number;
  titleStyle: ElementStyle;
  timeOffset?: number;
  allInitDates?: JSONInitDate[];
  onTitleStyleChange: (newStyle: ElementStyle) => void;
  onTitleChange: (newTitle: string) => void;
  onOffsetChange: (newOffset: number) => void;
  updateMapViewportToPoint: (newLocation: LocationResult) => void;
  disabled?: boolean;
};

export function MapLabel({
  title,
  titleStyle,
  timeOffset,
  allInitDates,
  displayTime,
  timezone,
  isLoading,
  gridHighlightType,
  gridHighlightId,
  onTitleStyleChange,
  onTitleChange,
  onOffsetChange,
  disabled,
  updateMapViewportToPoint,
}: MapLabelProps) {
  const { showHeader } = useOverlay();
  const { isExportActive } = useExportStatus();
  const desktop = useDesktop();
  const locationWindowId = instanceId(WindowKind.LocationSearchWindow);

  const [showStyleControls, setShowStyleControls] = useState(false);
  const [showOffsetControls, setShowOffsetControls] = useState(false);
  const [locationWindowOpen, setLocationWindowOpen] = useState(false);
  const styleControlRef = useRef<HTMLDivElement>(null);
  const offsetControlRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const styleControlCloseHandlerRef = useRef<(() => boolean) | null>(null);

  const requestStyleControlClose = () => {
    if (styleControlCloseHandlerRef.current) {
      const shouldClose = styleControlCloseHandlerRef.current();
      if (shouldClose === false) {
        return false;
      }
    }

    setShowStyleControls(false);
    setStyleControlDirty(false);
    return true;
  };

  const handleStyleControlSubmit = () => {
    requestStyleControlClose();
    return undefined;
  };

  const handleLocationChange = (newLocation: LocationResult) => {
    updateMapViewportToPoint(newLocation);
    setLocationWindowOpen(false);
  };

  const { setDirty: setStyleControlDirty } = useClickOutsideWithStateMultiple({
    onSubmit: handleStyleControlSubmit,
    isDirty: showStyleControls,
    refs: [styleControlRef, componentRef],
  });

  const handleOffsetControlSubmit = () => {
    setShowOffsetControls(false);
    return undefined;
  };

  const { setDirty: setOffsetControlDirty } = useClickOutsideWithStateMultiple({
    onSubmit: handleOffsetControlSubmit,
    isDirty: showOffsetControls,
    refs: [offsetControlRef, componentRef],
  });

  usePinnedGridHighlight({
    active: locationWindowOpen,
    type: gridHighlightType,
    id: gridHighlightId,
  });

  if (!displayTime) {
    return <></>;
  }

  const handleTitleClick = () => {
    if (showStyleControls) {
      requestStyleControlClose();
      return;
    }

    setShowStyleControls(true);
    setShowOffsetControls(false);

    setStyleControlDirty(true);
    setOffsetControlDirty(false);
  };

  const handleLocationClick = () => {
    if (!requestStyleControlClose()) {
      return;
    }

    setShowOffsetControls(false);
    setLocationWindowOpen(true);
    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: locationWindowId,
      windowKind: WindowKind.LocationSearchWindow,
      customProps: {
        onSelect: handleLocationChange,
        onCloseOverlay: () => setLocationWindowOpen(false),
        sourceCellName: title || undefined,
        showStationSearch: false,
        isAdvancedStationView: false,
      },
    });
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: locationWindowId,
      isVisible: true,
    });

    setOffsetControlDirty(false);
  };

  const handleDescriptionClick = () => {
    if (!requestStyleControlClose()) {
      return;
    }

    const willOpen = !showOffsetControls;
    setShowOffsetControls(willOpen);

    setOffsetControlDirty(willOpen);
  };

  return (
    <>
      <LoadingLabelFrame
        dataTestId="map-label"
        loadingTestId="map-label-loading"
        className={classNames("map-label", showHeader ? "map-label--header-expanded" : "")}
        ref={componentRef}
        isLoading={isLoading}
      >
        <div className={styles["label-row"]}>
          <LabelTitle
            title={title || "New Map"}
            onTitleChange={onTitleChange}
            onTitleClick={handleTitleClick}
            titleStyle={titleStyle}
            disabled={disabled}
          />
          {!isExportActive && (
            <MmTooltipWrapper tooltipText={t`Location search`} position="bottom" tail={true}>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
              <div
                className={`${styles["location-search-wrapper"]} ${
                  locationWindowOpen ? styles["location-search-wrapper--active"] : ""
                }`}
                onClick={handleLocationClick}
                style={{ fontSize: titleStyle?.fontSize || "1rem" }}
              >
                <div className={styles["label-icon-container"]} style={{ textDecoration: "none !important" }}>
                  <LocationSearchIcon />
                </div>
              </div>
            </MmTooltipWrapper>
          )}
          <MmTooltipWrapper
            tooltipText={t`Time Offset`}
            position="bottom"
            tail={true}
            disabled={showOffsetControls || showStyleControls || disabled}
          >
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
            <div
              className={styles["date-description-wrapper"]}
              style={disabled ? { pointerEvents: "none" } : {}}
              onClick={handleDescriptionClick}
            >
              <OffsetDescription offsetMinutes={timeOffset} titleStyle={titleStyle} disabled={disabled} />
              <span className="material-icons" style={{ fontSize: titleStyle?.fontSize || "1rem" }}>
                date_range
              </span>
              <LabelDescription
                timezone={timezone}
                datetimeStart={displayTime}
                titleStyle={titleStyle}
                disabled={disabled}
              />
            </div>
          </MmTooltipWrapper>
        </div>
        {allInitDates && allInitDates.length > 0 && (
          <div
            className={styles["model-runs"]}
            style={convertToCSSProperties({ ...titleStyle, fontSize: (titleStyle?.fontSize || 15) - 3 })}
          >
            <div className={styles["model-runs-header"]}>
              <div>Parameter</div>
              <div className={styles.divider} />
              <div>Model</div>
              <div className={styles.divider} />
              <div>Run</div>
            </div>
            {allInitDates?.map((item) => (
              <div className={styles["model-run"]} key={`${item?.model}-${item?.parameter}`}>
                <div>{item?.parameter}</div>
                <div className={styles.divider} />
                <div className="model-name">{item?.model}</div>
                <div className={styles.divider} />
                <div>
                  <LabelDescription
                    timezone={GuiTimeZone.utc}
                    datetimeStart={DateTime.fromISO(item?.initDate.toString())}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </LoadingLabelFrame>
      {showStyleControls && (
        <div className={classNames(styles["label-right"], "map-label-popover")} ref={styleControlRef}>
          <TitleStyleControl
            titleStyle={titleStyle}
            onStyleChange={onTitleStyleChange}
            registerCloseHandler={(handler) => {
              styleControlCloseHandlerRef.current = handler;
            }}
          />
        </div>
      )}
      {showOffsetControls && (
        <div className={classNames(styles["label-right"], "map-label-popover")} ref={offsetControlRef}>
          <OffsetControl offsetMinutes={timeOffset || 0} onOffsetChange={onOffsetChange} />
        </div>
      )}
    </>
  );
}
